export type TurnstileHandle = {
  /** Discard the spent token and solve a new challenge. */
  refresh: () => void;
};

export type TurnstileStatus = {
  token: string | null;
  hasFailed: boolean;
};

// Tokens are single-use: the one app-wide widget publishes here and each request spends and refreshes.
let widgetHandle: TurnstileHandle | null = null;
let status: TurnstileStatus = { token: null, hasFailed: false };
const listeners = new Set<(next: TurnstileStatus) => void>();

export const isTurnstileEnabled = (): boolean => Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY?.trim());

export const registerTurnstileHandle = (handle: TurnstileHandle | null): void => {
  widgetHandle = handle;
};

export const getTurnstileStatus = (): TurnstileStatus => status;

export const subscribeToTurnstile = (listener: (next: TurnstileStatus) => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

const publish = (next: TurnstileStatus): void => {
  status = next;
  listeners.forEach((listener) => listener(next));
};

export const setTurnstileToken = (token: string | null): void => {
  publish({ token, hasFailed: token ? false : status.hasFailed });
};

/** Sticky: once the widget gives up, forms must stop waiting on a token that will never arrive. */
export const setTurnstileFailed = (): void => {
  publish({ token: status.token, hasFailed: true });
};

/** A newly mounted widget deserves a clean slate, or an earlier flow's failure would condemn it. */
export const clearTurnstileFailure = (): void => {
  if (status.hasFailed) {
    publish({ token: status.token, hasFailed: false });
  }
};

/** Keeps a submit button disabled until the challenge has actually produced a token. */
export const isTurnstileBlocking = (state: TurnstileStatus): boolean =>
  isTurnstileEnabled() && !state.token && !state.hasFailed;

/**
 * Must outlast the widget's retry budget (MAX_CHALLENGE_FAILURES × RETRY_INTERVAL_MS), or the caller
 * gives up while Turnstile is still retrying — on a slow link a token has taken five attempts.
 */
const TOKEN_WAIT_TIMEOUT_MS = 45000;

/** Settles once a challenge produces a token or gives up — the caller then takes it, or fails open. */
const waitForChallenge = (): Promise<void> =>
  new Promise((resolve) => {
    // No widget mounted means no challenge is running: waiting would burn the whole budget for nothing.
    if (status.token || status.hasFailed || !widgetHandle) {
      resolve();

      return;
    }

    const timeoutId = window.setTimeout(() => {
      unsubscribe();
      resolve();
    }, TOKEN_WAIT_TIMEOUT_MS);

    const unsubscribe = subscribeToTurnstile((next) => {
      if (!next.token && !next.hasFailed) {
        return;
      }

      window.clearTimeout(timeoutId);
      unsubscribe();
      resolve();
    });
  });

/** Take and clear in one synchronous step, so two callers can never read the same token. */
const takeToken = (): string | null => {
  const spent = status.token;

  if (!spent) {
    return null;
  }

  publish({ token: null, hasFailed: status.hasFailed });
  widgetHandle?.refresh();

  return spent;
};

const spendToken = async (): Promise<{ turnstileToken?: string }> => {
  const held = takeToken();

  if (held) {
    return { turnstileToken: held };
  }

  const waitedFrom = Date.now();

  await waitForChallenge();
  const minted = takeToken();

  if (!minted) {
    // The elapsed time says which case this is: ~0ms means we gave up, the full budget means the
    // challenge never completed on this connection.
    console.warn(
      `[Turnstile] no token after ${Date.now() - waitedFrom}ms (gaveUp=${
        status.hasFailed
      }) — request goes out untokened`
    );

    return {};
  }

  return { turnstileToken: minted };
};

/** Overlapping requests queue: each waits for the previous spend, then for a challenge of its own. */
let pendingSpend: Promise<unknown> = Promise.resolve();

/** Spends the held token and starts the next challenge, so the following request has its own. */
export const withTurnstileToken = (): Promise<{ turnstileToken?: string }> => {
  if (!isTurnstileEnabled()) {
    return Promise.resolve({});
  }

  const spend = pendingSpend.then(spendToken);
  pendingSpend = spend.catch(() => undefined);

  return spend;
};
